import { PlusOutlined } from "@ant-design/icons";
import { useGetBriefDeliverables } from "@entities/brief/lib/hooks/useGetBriefDeliverables";
import DeliverablePackageCard from "@entities/brief/ui/Influencer/v2/DeliverablePackageCard";
import PackageForm from "@entities/brief/ui/Influencer/v2/PackageForm";
import listStyles from "@entities/brief/ui/Influencer/v2/styles.module.scss";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { IDelPackage } from "@shared/lib/types";
import { IBriefDeliverable } from "@shared/lib/types/briefInfluencerV2";
import { Button, Card, Tooltip, Typography } from "@shared/ui";
import { Checkbox } from "@shared/ui/Checkbox";
import { Flex, Row, Spin } from "antd";
import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.scss";

export function CampaignDeliverables({
  showTitle = true,
  isEditable,
  onClose,
  briefInfluencerIds,
  selectedDeliverableId,
  onSelectedDeliverableChange,
  initialBriefId,
  openNewPackageByDefault = false,
}: {
  showTitle?: boolean;
  isEditable?: boolean;
  onClose?: () => void;
  briefInfluencerIds?: string[];
  selectedDeliverableId?: string;
  onSelectedDeliverableChange?: (deliverable: IBriefDeliverable | null) => void;
  initialBriefId?: string;
  openNewPackageByDefault?: boolean;
}) {
  const { briefId: storeBriefId, brief } = useBriefStore();
  const briefId = initialBriefId || storeBriefId || "";

  const {
    deliverables: briefDeliverables,
    isFetching: isloadingDeliverables,
    isReFetching: isReFetchingDeliverables,
  } = useGetBriefDeliverables(briefId);

  const [selectedPackage, setSelectedPackage] = useState<
    IDelPackage | undefined
  >(undefined);
  const [operations, setOperations] = useState({
    add: false,
    edit: false,
    clone: false,
  });
  const [editingPackageId, setEditingPackageId] = useState<string | null>(null);
  const [packages, setPackages] = useState<IDelPackage[]>([]);
  const [selectedDeliverable, setSelectedDeliverable] =
    useState<IBriefDeliverable | null>(null);

  const topRef = useRef<HTMLDivElement | null>(null);
  const hasAutoOpenedNewPackage = useRef(false);

  useEffect(() => {
    if (!isloadingDeliverables && briefDeliverables && selectedDeliverableId) {
      setSelectedDeliverable(
        briefDeliverables.find(
          (item) => item.deliverableV2?._id === selectedDeliverableId
        )?.deliverableV2 || null
      );
    }
  }, [isloadingDeliverables, briefDeliverables, selectedDeliverableId]);

  useEffect(() => {
    if (briefDeliverables) {
      setPackages(briefDeliverables.map((item) => item.deliverableV2));
    }
  }, [briefDeliverables]);

  useEffect(() => {
    if (
      !openNewPackageByDefault ||
      hasAutoOpenedNewPackage.current ||
      isloadingDeliverables ||
      isReFetchingDeliverables
    ) {
      return;
    }

    if ((briefDeliverables?.length || 0) > 0) {
      return;
    }

    hasAutoOpenedNewPackage.current = true;
    setSelectedPackage(undefined);
    setOperations({ add: true, edit: false, clone: false });
  }, [
    openNewPackageByDefault,
    isloadingDeliverables,
    isReFetchingDeliverables,
    briefDeliverables,
  ]);

  const getNextPackageNumber = useMemo(() => {
    const maxSetNumber = Math.max(...packages.map((p) => p.setNumber || 0), 0);
    return maxSetNumber + 1;
  }, [packages]);

  const handleCopyPackage = (packageToCopy: IDelPackage) => {
    const copiedPackage: IDelPackage = {
      ...packageToCopy,
      setNumber: getNextPackageNumber,
      purchaseItems: packageToCopy.purchaseItems.map((del) => ({
        ...del,
        subTypes: { ...del.contentSubType },
      })),
    };

    setSelectedPackage(copiedPackage);
    setOperations({ add: false, edit: false, clone: true });
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const resetOperations = () => {
    setOperations({ add: false, edit: false, clone: false });
    setSelectedPackage(undefined);
    setEditingPackageId(null);
  };

  if (isloadingDeliverables) {
    return (
      <Row justify="center" style={{ paddingTop: 40, width: "100%" }}>
        <Spin />
      </Row>
    );
  }

  return (
    <Flex
      vertical
      gap={10}
      ref={topRef}
      className={classNames(styles.campaignDeliverables, {
        [styles.campaignDeliverablesEmbedded]: !showTitle,
      })}
      style={{ width: "100%" }}
    >
      {showTitle ? (
        <div className={styles.mainTitle}>Campaign Deliverables</div>
      ) : null}

      {!operations.add && !operations.edit && !operations.clone ? (
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          className={styles.createButton}
          onClick={() => {
            setSelectedPackage(undefined);
            setOperations({ add: true, edit: false, clone: false });
          }}
        >
          Create Deliverable Set
        </Button>
      ) : null}

      {(operations.add || operations.edit || operations.clone) && (
        <div
          className={classNames(styles.formWrapper, {
            [styles.formWrapperCompact]: !showTitle,
          })}
        >
          <PackageForm
            briefId={briefId}
            isEdit={operations.edit}
            isClone={operations.clone}
            selectedPackage={selectedPackage}
            isDefault={selectedPackage?._id === brief?.defaultDeliverableV2Id}
            onCancel={resetOperations}
            onSuccess={resetOperations}
            showTitle
          />
        </div>
      )}

      {packages.length > 0 ? (
        <>
          <Typography.Text className={styles.savedPackagesLabel}>
            SAVED PACKAGES
          </Typography.Text>

          {packages.map((pkg) => {
            const deliverableItem = briefDeliverables?.find(
              (item) => item.deliverableV2?._id === pkg._id
            );

            if (editingPackageId === pkg._id) {
              return (
                <div
                  key={pkg._id}
                  className={classNames(styles.formWrapper, {
                    [styles.formWrapperCompact]: !showTitle,
                  })}
                >
                  <PackageForm
                    briefId={briefId}
                    isEdit={true}
                    isClone={false}
                    selectedPackage={pkg}
                    isDefault={pkg._id === brief?.defaultDeliverableV2Id}
                    onCancel={() => setEditingPackageId(null)}
                    onSuccess={() => setEditingPackageId(null)}
                    showTitle
                  />
                </div>
              );
            }

            const packageCard = (
              <DeliverablePackageCard
                briefId={briefId}
                // hideOldWarning={pkg?.isOldFormat ? true : false}
                hideOldWarning={true}
                defaultDeliverableId={brief?.defaultDeliverableV2Id}
                deliverableInfo={deliverableItem}
                showMarkDefaultButton
                showEditIcon
                editFn={() => setEditingPackageId(pkg._id || null)}
                showCopyIcon={true}
                copyFn={() => handleCopyPackage(pkg)}
              />
            );

            if (!deliverableItem) {
              return packageCard;
            }

            const showCheckbox =
              Boolean(briefInfluencerIds?.length) && Boolean(isEditable);

            return (
              <Card
                key={pkg._id}
                style={{
                  width: "100%",
                  border:
                    showCheckbox && pkg._id === selectedDeliverable?._id
                      ? "2px solid #007AFF"
                      : undefined,
                  padding: 0,
                }}
                bodyStyle={{ padding: 0 }}
                className={listStyles.wrapperSelect}
              >
                <Flex
                  gap={10}
                  align="flex-start"
                  justify="flex-start"
                  vertical
                  style={{ marginBottom: 0, padding: 10 }}
                >
                  {showCheckbox ? (
                    <Checkbox
                      checked={pkg._id === selectedDeliverable?._id}
                      onChange={(event) => {
                        const newSelected = event.target.checked
                          ? deliverableItem.deliverableV2
                          : null;
                        setSelectedDeliverable(newSelected);
                        onSelectedDeliverableChange?.(newSelected);
                      }}
                      className={listStyles.selectPackageCheckbox}
                    >
                      <Tooltip
                        title={deliverableItem.usedByBriefInfluencers
                          .map((user) => user.socialMedia.username)
                          .join(", ")}
                      >
                        <Typography.Text ellipsis>
                          Sent to {deliverableItem.countUsed} creators
                          {deliverableItem.countUsed > 0 ? ": " : ""}
                          {deliverableItem.usedByBriefInfluencers
                            .map((user) => user.socialMedia.username)
                            .join(", ")}
                        </Typography.Text>
                      </Tooltip>
                    </Checkbox>
                  ) : (
                    <Tooltip
                      title={
                        deliverableItem.countUsed > 0
                          ? deliverableItem.usedByBriefInfluencers
                              .map((user) => user.socialMedia.username)
                              .join(", ")
                          : ""
                      }
                    >
                      <Typography.Text ellipsis>
                        Sent to {deliverableItem.countUsed} creators
                        {deliverableItem.countUsed > 0 ? ": " : ""}
                        {deliverableItem.usedByBriefInfluencers
                          .map((user) => user.socialMedia.username)
                          .join(", ")}
                      </Typography.Text>
                    </Tooltip>
                  )}
                  <div style={{ margin: 2, width: "100%" }}>{packageCard}</div>
                </Flex>
              </Card>
            );
          })}
        </>
      ) : null}
    </Flex>
  );
}
